import React, { useEffect } from 'react';
import { ArrowLeft, BookOpen, CircleHelp } from 'lucide-react';
import { useApp } from './AppContext';
import type { Screen } from '../App';

interface DocumentsCatalogPageProps {
    onNavigate: (screen: Screen) => void;
    onBack: () => void;
}

// Document list metadata

interface DocumentCard {
    id: string;
    screen: Screen;
    title: string;
    shortTitle: string;
    description: string;
    priority: boolean;
    category: string;
}

const DOCUMENTS: DocumentCard[] = [
    {
        id: 'periodic-check',
        screen: 'doc-periodic-check',
        title: 'Приказ Росгвардии №178 — Периодические проверки',
        shortTitle: 'Периодическая проверка',
        description: 'Порядок проведения периодической проверки ведомственной охраны онлайн: пригодность к применению оружия и спецсредств, упражнения по стрельбе, применение наручников и палки.',
        priority: true,
        category: 'Приказы Росгвардии'
    },
    {
        id: '77-fz',
        screen: 'doc-77fz',
        title: 'ФЗ-77 «О ведомственной охране»',
        shortTitle: 'ФЗ О ведомственной охране',
        description: 'Права и обязанности работников ведомственной охраны. Применение физической силы, специальных средств и огнестрельного оружия.',
        priority: true,
        category: 'Федеральные законы'
    },
    {
        id: 'weapons',
        screen: 'doc-weapons',
        title: 'ФЗ «Об оружии» №150-ФЗ',
        shortTitle: 'ФЗ Об оружии',
        description: 'Правила оборота, хранения и применения служебного оружия. Подходит для подготовки по запросам билеты ведомственной охраны с оружием.',
        priority: false,
        category: 'Федеральные законы'
    },
    {
        id: 'criminal-code',
        screen: 'doc-criminal-code',
        title: 'Статьи УК РФ для охранников',
        shortTitle: 'Статьи УК РФ',
        description: 'Уголовная ответственность за превышение полномочий, необходимая оборона, крайняя необходимость.',
        priority: false,
        category: 'Уголовный кодекс'
    }
];

export const DocumentsCatalogPage: React.FC<DocumentsCatalogPageProps> = ({ onNavigate, onBack }) => {
    const { getBackgroundClass } = useApp();

    // Update document title and meta for SEO
    useEffect(() => {
        // Add Schema.org structured data
        const schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.id = 'documents-catalog-schema';
        schemaScript.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Периодическая проверка ВОХР и документы для ведомственной охраны",
            "description": "Законы и нормативные документы для подготовки к экзамену ведомственной охраны: периодическая проверка ВОХР, ФЗ-77, ФЗ об оружии, приказы Росгвардии и билеты с оружием.",
            "url": "https://vohr-test.ru/documents",
            "hasPart": DOCUMENTS.map(doc => ({
                "@type": "LegalDocument",
                "name": doc.title,
                "description": doc.description
            }))
        });

        const existingSchema = document.getElementById('documents-catalog-schema');
        if (existingSchema) {
            existingSchema.remove();
        }
        document.head.appendChild(schemaScript);

        return () => {
            const schema = document.getElementById('documents-catalog-schema');
            if (schema) schema.remove();
        };
    }, []);

    return (
        <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
            {/* Header */}
            <header className="app-header-fixed flex items-center">
                <button
                    onClick={onBack}
                    className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
                    aria-label="Назад на главную"
                >
                    <ArrowLeft className="arrow-icon" />
                </button>
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
                    <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold glass-text truncate">
                        Полезная информация
                    </h1>
                </div>
                <button
                    onClick={() => onNavigate('document-help')}
                    className="ml-2 arrow-button flex-shrink-0"
                    aria-label="Справка"
                >
                    <CircleHelp className="arrow-icon" />
                </button>
            </header>

            {/* Main Content */}
            <main className="app-content-scrollable">
                {/* Intro Section for SEO */}
                <section className="mb-6">
                    <p className="glass-text-secondary text-sm sm:text-base mb-4">
                        Полный сборник законов и нормативных документов для подготовки к экзамену, периодической проверке ведомственной охраны и билетам ВОХР с оружием.
                    </p>
                </section>

                {/* Priority Documents */}
                <section className="mb-8">
                    <h2 className="text-base sm:text-lg font-semibold glass-text mb-4 flex items-center gap-2">
                        Приоритетные документы
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {DOCUMENTS.filter(doc => doc.priority).map(doc => (
                            <article
                                key={doc.id}
                                className="glass-panel rounded-xl glass-interactive p-4 sm:p-5 cursor-pointer"
                                onClick={() => onNavigate(doc.screen)}
                            >
                                <div className="flex-1 min-w-0">
                                    <span className="glass-badge-default text-xs mb-2 inline-block">{doc.category}</span>
                                    <h3 className="font-semibold glass-text text-sm sm:text-base mb-2 line-clamp-2">
                                        {doc.title}
                                    </h3>
                                    <p className="glass-text-secondary text-xs sm:text-sm line-clamp-3">
                                        {doc.description}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* Other Documents */}
                <section className="mb-8">
                    <h2 className="text-base sm:text-lg font-semibold glass-text mb-4">
                        Дополнительные документы
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {DOCUMENTS.filter(doc => !doc.priority).map(doc => (
                            <article
                                key={doc.id}
                                className="glass-panel rounded-xl glass-interactive p-4 sm:p-5 cursor-pointer"
                                onClick={() => onNavigate(doc.screen)}
                            >
                                <div className="flex-1 min-w-0">
                                    <span className="glass-badge-default text-xs mb-2 inline-block">{doc.category}</span>
                                    <h3 className="font-semibold glass-text text-sm sm:text-base mb-2 line-clamp-2">
                                        {doc.title}
                                    </h3>
                                    <p className="glass-text-secondary text-xs sm:text-sm line-clamp-3">
                                        {doc.description}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

            </main>
        </div>
    );
};
